import { 
  Professional, 
  Collaborator, 
  Appointment, 
  ServiceInvoice, 
  ProfessionalDashboardData,
  AppointmentCalendarData 
} from '../types/professionalTypes';

class ProfessionalService {
  // Mock data for development
  private mockProfessional: Professional = {
    _key: 'PROF_001',
    user_id: 'USER_PROF_001',
    professional_type: 'veterinarian',
    license_number: 'CRMV-SP-12345',
    specializations: ['Dairy Cattle', 'Reproduction', 'Health Management'],
    service_menu: [
      {
        service_name: 'Consulta Veterinária Geral',
        description: 'Exame clínico completo do rebanho',
        base_price: 150.00,
        price_unit: 'per_visit',
        estimated_duration: 120
      },
      {
        service_name: 'Vacinação',
        description: 'Aplicação de vacinas conforme protocolo',
        base_price: 25.00,
        price_unit: 'per_animal',
        estimated_duration: 5
      },
      {
        service_name: 'Inseminação Artificial',
        description: 'Procedimento de IA com sêmen certificado',
        base_price: 80.00,
        price_unit: 'per_animal',
        estimated_duration: 15
      }
    ],
    contact_info: {
      phone: '+5511987654321',
      email: 'dra.ana@vetservice.com.br',
      whatsapp: '+5511987654321'
    },
    availability: {
      working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      working_hours: {},
      service_radius_km: 50
    },
    credentials: {
      education: ['Medicina Veterinária - USP'],
      certifications: ['Especialização em Reprodução Bovina', 'Certificação IATF'],
      years_experience: 8
    },
    rating: 4.8,
    total_reviews: 127,
    is_available: true,
    created_at: '2023-01-15T08:00:00Z',
    updated_at: '2024-01-15T10:30:00Z'
  };

  private mockAppointments: Appointment[] = [
    {
      _key: 'APPT_001',
      professional_id: 'PROF_001',
      collaborator_id: 'COLLAB_001',
      appointment_type: 'routine_check',
      service_details: {
        service_name: 'Consulta Veterinária Geral',
        description: 'Exame do rebanho leiteiro',
        estimated_duration: 120,
        animals_involved: ['CATTLE_001', 'CATTLE_002'],
        equipment_needed: ['Estetoscópio', 'Termômetro']
      },
      scheduling: {
        scheduled_date: '2024-01-20',
        scheduled_time: '08:00',
        estimated_end_time: '10:00',
        timezone: 'America/Sao_Paulo'
      },
      location: {
        farm_id: 'FARM_001',
        specific_location: 'Curral Principal',
        address: 'Fazenda Boi Verde, Piracicaba/SP'
      },
      status: 'scheduled',
      priority: 'normal',
      created_by: 'USER_FARM_001',
      cost_estimate: 150.00,
      notes: 'Verificar condição do rebanho após período de chuvas',
      created_at: '2024-01-18T14:30:00Z',
      updated_at: '2024-01-18T14:30:00Z'
    },
    {
      _key: 'APPT_002',
      professional_id: 'PROF_001',
      collaborator_id: 'COLLAB_002',
      appointment_type: 'vaccination',
      service_details: {
        service_name: 'Vacinação',
        description: 'Vacinação contra febre aftosa',
        estimated_duration: 180,
        animals_involved: [],
        equipment_needed: ['Seringas', 'Vacinas']
      },
      scheduling: {
        scheduled_date: '2024-01-22',
        scheduled_time: '07:00',
        estimated_end_time: '10:00',
        timezone: 'America/Sao_Paulo'
      },
      location: {
        farm_id: 'FARM_COOP_001',
        specific_location: 'Área de Manejo',
        address: 'Cooperativa do Leite do Sul, Campinas/SP'
      },
      status: 'confirmed',
      priority: 'high',
      created_by: 'USER_COOP_001',
      cost_estimate: 750.00,
      notes: 'Campanha de vacinação obrigatória',
      created_at: '2024-01-19T09:15:00Z',
      updated_at: '2024-01-19T16:20:00Z'
    }
  ];

  private mockInvoices: ServiceInvoice[] = [
    {
      _key: 'INV_001',
      professional_id: 'PROF_001',
      appointment_id: 'APPT_001',
      invoice_number: 'PROF-2024-001',
      invoice_date: '2024-01-15',
      due_date: '2024-02-15',
      services: [
        {
          service_name: 'Consulta Veterinária Geral',
          description: 'Exame clínico completo',
          quantity: 1,
          unit_price: 150.00,
          total_price: 150.00
        }
      ],
      financial_summary: {
        subtotal: 150.00,
        tax_amount: 0.00,
        discount_amount: 0.00,
        total_amount: 150.00
      },
      payment_info: {
        payment_status: 'pending'
      },
      created_at: '2024-01-15T16:00:00Z',
      updated_at: '2024-01-15T16:00:00Z'
    }
  ];

  async getProfessionalProfile(professionalId: string): Promise<Professional> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return this.mockProfessional;
  }

  async getProfessionalDashboard(professionalId: string): Promise<ProfessionalDashboardData> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const today = new Date().toISOString().split('T')[0];
    const todayAppointments = this.mockAppointments.filter(
      apt => apt.scheduling.scheduled_date === today
    );
    
    const upcomingAppointments = this.mockAppointments.filter(
      apt => apt.scheduling.scheduled_date > today
    );

    return {
      upcoming_appointments: upcomingAppointments,
      today_appointments: todayAppointments,
      pending_invoices: this.mockInvoices.filter(inv => inv.payment_info?.payment_status === 'pending'),
      monthly_earnings: 2450.00,
      total_clients: 5,
      appointments_this_month: 12,
      average_rating: 4.8,
      recent_activities: [
        { type: 'appointment_completed', message: 'Consulta concluída na Fazenda Boi Verde', date: '2024-01-18' },
        { type: 'invoice_paid', message: 'Fatura PROF-2024-001 paga', date: '2024-01-17' },
        { type: 'new_client', message: 'Nova parceria com Cooperativa Leite Sul', date: '2024-01-16' }
      ]
    };
  }

  async getAppointments(professionalId: string, dateRange?: { start: string; end: string }): Promise<AppointmentCalendarData> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    return {
      appointments: this.mockAppointments,
      availability_slots: [
        { date: '2024-01-21', available_times: ['08:00', '14:00', '16:00'] },
        { date: '2024-01-23', available_times: ['07:00', '09:00', '15:00'] },
        { date: '2024-01-24', available_times: ['08:00', '10:00', '14:00'] }
      ]
    };
  }

  async getInvoices(professionalId: string, filters?: any): Promise<ServiceInvoice[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return this.mockInvoices;
  }

  async createAppointment(appointment: Partial<Appointment>): Promise<Appointment> {
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const newAppointment: Appointment = {
      _key: `APPT_${Date.now()}`,
      professional_id: appointment.professional_id!,
      collaborator_id: appointment.collaborator_id!,
      appointment_type: appointment.appointment_type!,
      service_details: appointment.service_details!,
      scheduling: appointment.scheduling!,
      location: appointment.location,
      status: 'scheduled',
      priority: appointment.priority || 'normal',
      created_by: appointment.created_by!,
      cost_estimate: appointment.cost_estimate,
      notes: appointment.notes,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    this.mockAppointments.push(newAppointment);
    return newAppointment;
  }

  async createInvoice(invoice: Partial<ServiceInvoice>): Promise<ServiceInvoice> {
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const newInvoice: ServiceInvoice = {
      _key: `INV_${Date.now()}`,
      professional_id: invoice.professional_id!,
      appointment_id: invoice.appointment_id,
      invoice_number: invoice.invoice_number!,
      invoice_date: invoice.invoice_date!,
      due_date: invoice.due_date!,
      services: invoice.services!,
      medications_supplies: invoice.medications_supplies,
      financial_summary: invoice.financial_summary!,
      payment_info: invoice.payment_info || { payment_status: 'pending' },
      notes: invoice.notes,
      attachments: invoice.attachments,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    this.mockInvoices.push(newInvoice);
    return newInvoice;
  }

  async updateAppointmentStatus(appointmentId: string, status: Appointment['status'], completionDetails?: any): Promise<Appointment> {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const appointmentIndex = this.mockAppointments.findIndex(apt => apt._key === appointmentId);
    if (appointmentIndex !== -1) {
      this.mockAppointments[appointmentIndex].status = status;
      this.mockAppointments[appointmentIndex].updated_at = new Date().toISOString();
      
      if (completionDetails) {
        this.mockAppointments[appointmentIndex].completion_details = completionDetails;
      }
      
      return this.mockAppointments[appointmentIndex];
    }
    
    throw new Error('Appointment not found');
  }

  // Professional search for organizations
  async searchProfessionals(filters: {
    professional_type?: string;
    specializations?: string[];
    location?: { lat: number; lng: number; radius_km: number };
    availability?: string[];
  }): Promise<Professional[]> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    let results = [this.mockProfessional];
    
    if (filters.professional_type && filters.professional_type !== this.mockProfessional.professional_type) {
      results = [];
    }
    
    return results;
  }
}

export const professionalService = new ProfessionalService();